import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * @typedef {HTMLElement & {
 *   select: (input: number | string | { id: string }, event?: Event, options?: { animate?: boolean }) => Promise<void>,
 *   pause: () => void,
 *   play: (interval?: number) => void,
 *   suspend: () => void,
 *   paused: boolean,
 *   current: number,
 *   autoplay: boolean,
 *   slides?: HTMLElement[] | undefined,
 * }} TwinSlideshowElement
 */

/**
 * Syncs two pane slideshows inside DAC Twin Slideshow when the `sync` attribute is set.
 * Listens for `slideshow:select` on either pane and mirrors the index to the other,
 * with a re-entrancy lock so A→B does not bounce back as B→A.
 *
 * When sync is on:
 * - Followers never own native autoplay (avoids dual timers).
 * - Optional host `autoplay` attribute drives both panes together with animated
 *   selects (including last→first as a reverse slide). Infinite is kept off so
 *   we avoid the theme's DOM-reorder loop path that races with sync.
 * - Left pane keeps a single play/pause control; its play/pause methods are
 *   patched so the host timer (not the pane interval) starts and stops.
 *
 * @extends {Component}
 */
export class DacTwinSlideshowComponent extends Component {
  static get observedAttributes() {
    return ['sync', 'autoplay'];
  }

  /** @type {boolean} */
  #syncing = false;

  /** @type {boolean} */
  #hostPaused = false;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #autoplayTimer;

  /** @type {WeakSet<TwinSlideshowElement>} */
  #patchedControls = new WeakSet();

  /** Settle time after an animated select before we re-assert scroll position. */
  static #ANIMATION_SETTLE_MS = 450;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    // Panes may upgrade after the host; defer so autoplay patching sees live methods.
    queueMicrotask(() => {
      this.#ensureSingleAutoplayDriver();
      this.#syncHostAutoplay();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#stopHostAutoplay();
  }

  /**
   * @param {string} name
   * @param {string | null} _oldValue
   * @param {string | null} _newValue
   */
  attributeChangedCallback(name, _oldValue, _newValue) {
    if (name === 'sync' || name === 'autoplay') {
      if (!this.#hostAutoplayInterval) {
        this.#hostPaused = false;
      }
      this.#ensureSingleAutoplayDriver();
      this.#syncHostAutoplay();
    }
  }

  /**
   * @returns {boolean}
   */
  get #syncEnabled() {
    return this.hasAttribute('sync');
  }

  /**
   * Host autoplay interval in ms (from autoplay="N" seconds), or undefined.
   * @returns {number | undefined}
   */
  get #hostAutoplayInterval() {
    if (!this.#syncEnabled) return undefined;

    const raw = this.getAttribute('autoplay');
    if (raw == null || raw === '') return undefined;

    const seconds = parseInt(raw, 10);
    if (Number.isNaN(seconds) || seconds < 1) return undefined;

    return seconds * 1000;
  }

  /**
   * @returns {TwinSlideshowElement[]}
   */
  #getPaneSlideshows() {
    /** @type {TwinSlideshowElement[]} */
    const slideshows = [];

    for (const el of this.querySelectorAll(':scope > .dac-twin-pane slideshow-component')) {
      if (!(el instanceof HTMLElement)) continue;
      if (typeof /** @type {TwinSlideshowElement} */ (el).select !== 'function') continue;
      slideshows.push(/** @type {TwinSlideshowElement} */ (el));
    }

    return slideshows;
  }

  /**
   * Pane that renders the shared play/pause control (left, with icon buttons).
   * @returns {TwinSlideshowElement | undefined}
   */
  #getControlSlideshow() {
    return this.#getPaneSlideshows().find((slideshow) =>
      Boolean(slideshow.querySelector('.slideshow-control.icon-pause, .slideshow-control.icon-play'))
    );
  }

  /**
   * Strip competing pane timers when sync is on. Keep `[autoplay]` on the control
   * pane so theme CSS can toggle play/pause icons; patch play/pause to drive host.
   */
  #ensureSingleAutoplayDriver() {
    if (!this.#syncEnabled) return;

    const control = this.#hostAutoplayInterval ? this.#getControlSlideshow() : undefined;

    for (const slideshow of this.#getPaneSlideshows()) {
      // Infinite last→first uses a placeholder reorder that races with sync.
      slideshow.removeAttribute('infinite');

      if (slideshow === control) {
        // Keep [autoplay] for theme play/pause icon CSS; kill native interval only.
        slideshow.suspend();
        this.#patchControlAutoplay(slideshow);
        continue;
      }

      if (slideshow.autoplay) {
        slideshow.pause();
        slideshow.removeAttribute('autoplay');
      }
    }
  }

  /**
   * Route the control pane's play/pause buttons to the host timer.
   * @param {TwinSlideshowElement} slideshow
   */
  #patchControlAutoplay(slideshow) {
    if (this.#patchedControls.has(slideshow)) return;
    this.#patchedControls.add(slideshow);

    const originalPause = slideshow.pause.bind(slideshow);
    const originalPlay = slideshow.play.bind(slideshow);

    slideshow.pause = () => {
      if (!this.#syncEnabled || !this.#hostAutoplayInterval) {
        originalPause();
        return;
      }

      this.#hostPaused = true;
      this.#stopHostAutoplay();
      originalPause();
    };

    slideshow.play = (interval) => {
      if (!this.#syncEnabled || !this.#hostAutoplayInterval) {
        originalPlay(interval);
        return;
      }

      this.#hostPaused = false;
      slideshow.paused = false;
      // Never start the pane's own interval — host owns pacing.
      slideshow.suspend();
      this.#syncHostAutoplay();
    };
  }

  #stopHostAutoplay() {
    if (this.#autoplayTimer !== undefined) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = undefined;
    }
  }

  /**
   * When sync + host autoplay are set, advance every pane to the next index together.
   */
  #syncHostAutoplay() {
    this.#stopHostAutoplay();

    const interval = this.#hostAutoplayInterval;
    if (!interval || this.#hostPaused) return;

    // Ensure control pane is not running a competing native timer.
    this.#getControlSlideshow()?.suspend();

    this.#autoplayTimer = setInterval(() => {
      if (this.matches(':hover') || document.hidden || this.#syncing) return;

      const slideshows = this.#getPaneSlideshows();
      const driver = slideshows[0];
      if (!driver) return;

      const count = driver.slides?.length ?? 0;
      if (count < 2) return;

      const nextIndex = (driver.current + 1) % count;
      this.#goToIndex(nextIndex);
    }, interval);
  }

  /**
   * Move every pane to the same index (used by host autoplay).
   * Always animates, including last→first (adjacent reverse scroll with infinite off).
   * @param {number} index
   */
  #goToIndex = async (index) => {
    if (this.#syncing) return;

    const slideshows = this.#getPaneSlideshows();
    if (slideshows.length < 1) return;

    this.#syncing = true;
    try {
      /** @type {Promise<void>[]} */
      const pending = [];

      for (const slideshow of slideshows) {
        const count = slideshow.slides?.length ?? 0;
        if (count < 1) continue;

        const paneTarget = ((index % count) + count) % count;
        if (slideshow.current === paneTarget) continue;

        pending.push(this.#selectPane(slideshow, paneTarget, true));
      }

      await Promise.all(pending);
    } finally {
      this.#syncing = false;
    }
  };

  /**
   * @param {TwinSlideshowElement} slideshow
   * @param {number} targetIndex
   * @param {boolean} animate
   */
  #selectPane = async (slideshow, targetIndex, animate) => {
    await slideshow.select(targetIndex, undefined, { animate });

    // Wait for scroll to settle, then re-assert position if snap/layout drifted.
    if (animate) {
      await new Promise((resolve) => setTimeout(resolve, DacTwinSlideshowComponent.#ANIMATION_SETTLE_MS));
    } else {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    this.#forceScrollToIndex(slideshow, targetIndex);
  };

  /**
   * @param {TwinSlideshowElement} slideshow
   * @param {number} targetIndex
   */
  #forceScrollToIndex(slideshow, targetIndex) {
    const slide = slideshow.slides?.[targetIndex];
    const scroller = slideshow.querySelector('slideshow-slides');
    if (!(slide instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    const targetLeft = slide.offsetLeft;
    if (Math.abs(scroller.scrollLeft - targetLeft) < 2) return;

    scroller.scrollLeft = targetLeft;
  }

  /**
   * @param {Event} event
   */
  #handleSelect = async (event) => {
    if (!this.#syncEnabled || this.#syncing) return;
    if (!(event instanceof SlideshowSelectEvent)) return;

    const source = event.target;
    if (!(source instanceof HTMLElement) || source.tagName !== 'SLIDESHOW-COMPONENT') return;

    const slideshows = this.#getPaneSlideshows();
    if (slideshows.length < 2 || !slideshows.includes(/** @type {TwinSlideshowElement} */ (source))) return;

    this.#ensureSingleAutoplayDriver();

    const index = event.detail?.index;
    if (typeof index !== 'number' || Number.isNaN(index)) return;

    this.#syncing = true;
    try {
      /** @type {Promise<void>[]} */
      const pending = [];

      for (const slideshow of slideshows) {
        if (slideshow === source) continue;

        const slideCount = slideshow.slides?.length ?? 0;
        if (slideCount < 1) continue;

        const targetIndex = ((index % slideCount) + slideCount) % slideCount;
        if (slideshow.current === targetIndex) continue;

        // Mirror with the same motion style as the source step when adjacent.
        const animate = Math.abs(targetIndex - slideshow.current) === 1;
        pending.push(this.#selectPane(slideshow, targetIndex, animate));
      }

      await Promise.all(pending);
    } finally {
      this.#syncing = false;
    }
  };
}

if (!customElements.get('dac-twin-slideshow-component')) {
  customElements.define('dac-twin-slideshow-component', DacTwinSlideshowComponent);
}
